import styled, { DefaultTheme } from "styled-components";
import { TextField } from "../shared/TextField/TextField";
import { Button } from "../shared/Button/Button";

export const AttestationBanner = styled.div`
  border-left: 4px solid ${({ theme }) => theme.redMedium};
  padding: 16px 20px;
  background: ${({ theme }) => theme.redPastel};
`;

export const AttestationCheckboxRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 24px;
`;

export const AttestationCheckboxLabel = styled.label`
  line-height: 1.5;
  cursor: pointer;
`;

export const AttestationActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const getDateColor = (
  theme: DefaultTheme,
  error?: string,
  noDateValue?: boolean,
) => {
  if (error) return theme.redDark;
  if (noDateValue) return theme.neutral500;
  return theme.black;
};

export const GlobalSearchTextField = styled(TextField)<{
  $noDateValue?: boolean;
  $showHelpertext?: boolean;
  $invalidAsterisk?: boolean;
}>`
  flex: 1;
  color: ${({ theme }) => theme.greyLight};
  width: 100%;
  font-family: light;

  input::-webkit-datetime-edit {
    color: ${({ theme, error, $noDateValue }) =>
      getDateColor(theme, error, $noDateValue)};
  }

  && {
    min-width: 120px;
    color: ${({ theme }) => theme.black};
    font-family: light;

    .MuiInputLabel-root {
      color: ${({ theme }) => theme.neutral700};
      font-family: light;
      line-height: inherit;
      &.Mui-disabled {
        color: ${({ theme }) => theme.neutral400};
      }
    }

    .MuiInputLabel-asterisk {
      color: ${({ theme, $invalidAsterisk }) =>
        $invalidAsterisk ? theme.redDark : theme.neutral700};
    }

    .Mui-focused {
      color: ${({ theme }) => theme.black};
    }

    .MuiInput-input {
      font-family: light;
      font-size: 1rem;
    }

    .MuiInput-root:after {
      border-bottom: ${({ theme }) => `1px solid ${theme.greyLight}`};
    }

    .Mui-error.MuiInputLabel-root {
      color: ${({ theme }) => theme.redDark} !important;
    }
    .Mui-error.MuiInputLabel-asterisk {
      color: ${({ theme }) => theme.redDark} !important;
    }
    .Mui-error {
      .MuiInput-input {
        color: ${({ theme }) => theme.redDark} !important;
      }
      :after {
        border-bottom: 1px solid ${({ theme }) => theme.redDark} !important;
      }
    }
  }

  .MuiFormHelperText-root {
    display: ${({ $showHelpertext }) => ($showHelpertext ? undefined : "none")};
    color: ${({ theme }) => theme.neutral700} !important;
  }

  .MuiFormHelperText-root.Mui-error {
    display: ${({ $showHelpertext }) => ($showHelpertext ? undefined : "none")};
    color: ${({ $showHelpertext, theme }) =>
      $showHelpertext ? theme.redDark : undefined} !important;
  }
`;

export const AttestationContent = styled.div`
  padding: 24px;
`;

export const SearchForm = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 32px 32px 16px;
`;

export const SearchButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-left: 32px;
  flex-shrink: 0;
`;

export const SearchFindButton = styled(Button)`
  && {
    line-height: 1;
    height: 44px;
    padding: 0 20px;
  }
  &&.Mui-disabled {
    opacity: 0.5;
    color: ${({ theme }) => theme.redMedium};
    background: ${({ theme }) => theme.white};
    border-color: ${({ theme }) => theme.redMedium};
  }
`;

export const SearchFindButtonWrapper = styled.span<{ $loading?: boolean }>`
  position: relative;
  display: inline-block;
  ${({ $loading }) =>
    $loading
      ? `
    button { color: transparent !important; }
  `
      : ""}
`;

export const SearchFindButtonSpinner = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: ${({ theme }) => theme.redMedium};
  font-size: 22px;
`;

export const SearchClearButton = styled.button`
  background: none;
  border: none;
  padding: 0 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.redMedium};
  display: flex;
  align-items: center;
  font-size: 24px;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.redLight};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const ConfirmationContent = styled.div`
  padding: 8px 24px 16px;
  line-height: 1.5;
`;

export const ConfirmationBoldLine = styled.div`
  font-family: "bold";
  font-weight: 700;
  color: ${({ theme }) => theme.neutral700};
`;

export const ConfirmationMessage = styled.div`
  padding-top: 10px;
`;

export const ModalSpacerFooter = styled.div`
  height: 48px;
  border-top: 1px solid ${({ theme }) => theme.neutral200};
`;

export const ConfirmationFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-top: 16px;
`;

export const ConfirmationFooterButton = styled.button<{
  $isSubmitting?: boolean;
}>`
  background: none;
  border: none;
  color: ${({ $isSubmitting, theme }) =>
    $isSubmitting ? "transparent" : theme.redMedium};
  font-family: "bold";
  font-weight: 700;
  font-size: 0.875rem;
  letter-spacing: 0.08em;
  cursor: pointer;
  padding: 8px 20px;

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:disabled {
    color: ${({ $isSubmitting, theme }) =>
      $isSubmitting ? "transparent" : theme.neutral300};
    cursor: default;
  }
`;

export const ConfirmationActionButtonWrapper = styled.span`
  position: relative;
  display: inline-block;
`;

export const ConfirmationFooterSpinner = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  font-size: 18px;
  color: ${({ theme }) => theme.redMedium};
`;

export const ConfirmationErrorText = styled.span`
  color: ${({ theme }) => theme.redDark};
  font-size: 0.875rem;
  padding-left: 20px;
`;

export const SearchResultsSection = styled.div`
  padding: 8px 24px 24px;
`;

export const SearchTableWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.neutral200};
  border-radius: 8px;
  overflow: hidden;
`;

export const SearchTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

export const SearchTableHeaderCell = styled.th`
  text-align: left;
  padding: 8px;
  white-space: nowrap;
  border-bottom: 1px solid ${({ theme }) => theme.neutral200};
`;

export const SearchTableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.neutral100};
  height: 44px;
`;

export const SearchTableCell = styled.td`
  padding: 0 8px;
  height: 44px;
  box-sizing: border-box;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
`;

export const SearchTableActionCell = styled(SearchTableCell)`
  text-align: center;
`;

export const ConfirmationSearchTable = styled(SearchTable)`
  margin-top: 12px;
`;

export const ConfirmationSearchTableRow = styled(SearchTableRow)`
  border-bottom: none;
`;

export const SearchEmptyState = styled.div`
  text-align: center;
  padding: 32px 0;
`;

export const SearchSingleRowCell = styled.td`
  padding: 0 8px;
  height: 44px;
  box-sizing: border-box;
  text-align: center;
  vertical-align: middle;
`;

export const AttestationSubmitButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.redMedium};
  font-weight: 700;
  font-size: 0.875rem;
  letter-spacing: 0.08em;
  cursor: pointer;
  padding: 8px 0;

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const DoneFooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 24px 8px;
`;

export const AffiliateActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0 8px;
  gap: 16px;
`;

export const AccountPickerWrapper = styled.div`
  flex: 1;
`;

export const skeletonSx = {
  borderRadius: 1,
  bgcolor: "rgba(0, 0, 0, 0.08)",
  "&::after": {
    background:
      "linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.10), transparent)",
  },
};
